import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, ArrowRight, Mail, X } from "lucide-react";
import { celebrate } from "../lib/confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAMILY_OPTIONS = [
  ["teint", "Teint"],
  ["yeux", "Yeux"],
  ["levres", "Lèvres"],
  ["soins-visage", "Soins visage"],
  ["soins-corps", "Soins corps"],
  ["soins-cheveux", "Soins cheveux"],
  ["parfums", "Parfums"],
  ["complements-alimentaires", "Compléments"],
  ["accessoires", "Accessoires"],
];

const FIRST_QUESTION = {
  key: "family",
  label: "Quelle famille vous intéresse aujourd'hui ?",
  options: FAMILY_OPTIONS,
};

// Family-specific questions (3 each), ending with the universal budget step
const FAMILY_QUESTIONS = {
  teint: [
    { key: "skin_type", label: "Votre type de peau",
      options: [["seche","Sèche"],["normale","Normale"],["mixte","Mixte"],["grasse","Grasse"],["sensible","Sensible"]] },
    { key: "coverage", label: "Couvrance souhaitée",
      options: [["legere","Légère, effet bonne mine"],["moyenne","Moyenne, modulable"],["haute","Haute couvrance"]] },
    { key: "finish", label: "Le fini idéal",
      options: [["mat","Mat velours"],["satine","Satiné naturel"],["glow","Glow lumineux"]] },
  ],
  yeux: [
    { key: "look", label: "L'effet recherché",
      options: [["naturel","Naturel"],["sophistique","Sophistiqué"],["glamour","Glamour intense"]] },
    { key: "concern_eyes", label: "Votre préoccupation",
      options: [["cernes","Cernes"],["poches","Poches"],["cils-fins","Cils fins"]] },
    { key: "intensity", label: "Intensité du regard",
      options: [["legere","Discrète"],["moyenne","Maîtrisée"],["haute","Spectaculaire"]] },
  ],
  levres: [
    { key: "lip_texture", label: "Texture préférée",
      options: [["mat-texture","Mat"],["satin-texture","Satin"],["gloss-texture","Gloss pulpy"]] },
    { key: "lip_color", label: "Famille de couleurs",
      options: [["nude","Nudes"],["rose","Roses"],["rouge","Rouges"],["baie","Baies & prunes"],["audacieuse","Audacieuses"]] },
    { key: "lip_concern", label: "Préoccupation",
      options: [["lèvres-seches","Lèvres sèches"],["hydratation","Hydratation"],["fermete","Volume / repulpant"]] },
  ],
  "soins-visage": [
    { key: "skin_type", label: "Votre type de peau",
      options: [["seche","Sèche"],["normale","Normale"],["mixte","Mixte"],["grasse","Grasse"],["sensible","Sensible"]] },
    { key: "concern", label: "Préoccupation principale",
      options: [["eclat","Éclat"],["anti-age","Anti-âge"],["hydratation","Hydratation"],["taches","Taches"],["imperfections","Imperfections"]] },
    { key: "moment", label: "Moment de la routine",
      options: [["jour","Routine de jour"],["nuit","Routine de nuit"],["complete","Routine complète"]] },
  ],
  "soins-corps": [
    { key: "body_type", label: "Votre peau",
      options: [["seche","Sèche"],["normale","Normale"],["sensible","Sensible"]] },
    { key: "body_goal", label: "Effet recherché",
      options: [["nutrition-corps","Nutrition"],["parfum-corps","Sillage parfumé"],["fermete-corps","Fermeté"]] },
    { key: "body_format", label: "Format préféré",
      options: [["gel","Gel douche"],["lait","Lait corporel"],["huile","Huile"],["creme-corps","Crème / beurre"]] },
  ],
  "soins-cheveux": [
    { key: "hair_type", label: "Type de cheveux",
      options: [["raides","Raides"],["ondules","Ondulés"],["boucles","Bouclés"],["frises","Frisés"],["crepus","Crépus"]] },
    { key: "hair_state", label: "État capillaire",
      options: [["cheveux-gras","Gras"],["cheveux-secs","Secs"],["cheveux-abimes","Abîmés"],["chute","Chute / densité"],["frisottis","Frisottis"]] },
    { key: "hair_step", label: "Étape de routine",
      options: [["lavage","Lavage"],["soin-cheveux","Soin profond"],["finition","Finition / coiffage"]] },
  ],
  parfums: [
    { key: "perfume_for", label: "Pour qui ?",
      options: [["femme","Femme"],["homme","Homme"],["unisexe","Unisexe"],["enfant","Enfant"]] },
    { key: "olfactory", label: "Famille olfactive",
      options: [["floral","Floral"],["boise","Boisé"],["oriental","Oriental"],["hesperide","Hespéridé"],["gourmand","Gourmand"]] },
    { key: "occasion", label: "L'occasion",
      options: [["quotidien","Quotidien"],["soir","Soir"],["ete","Été"],["hiver","Hiver"]] },
  ],
  "complements-alimentaires": [
    { key: "supp_goal", label: "Votre objectif",
      options: [["objectif-peau","Peau"],["objectif-cheveux","Cheveux"],["objectif-ongles","Ongles"],["objectif-energie","Énergie"],["objectif-sommeil","Sommeil"],["objectif-immunite","Immunité"]] },
    { key: "supp_format", label: "Format préféré",
      options: [["gummies-fmt","Gummies"],["gelules-fmt","Gélules"],["bandes-fmt","Bandes"]] },
    { key: "supp_duration", label: "Durée de cure",
      options: [["1mois","1 mois"],["3mois","3 mois"],["continu","En continu"]] },
  ],
  "ambiance-maison": [],  // retired category — kept as empty stub so
                          // legacy quiz state (if any) resolves harmlessly.
  accessoires: [
    { key: "acc_type", label: "Quel accessoire ?",
      options: [["pinceaux-acc","Pinceaux"],["eponges-acc","Éponges"],["recourbe-acc","Recourbe-cils"],["trousse-acc","Trousse"]] },
    { key: "acc_level", label: "Votre niveau",
      options: [["debutante","Débutante"],["amatrice","Amatrice"],["pro","Professionnelle"]] },
    { key: "acc_use", label: "Usage principal",
      options: [["visage-acc","Visage"],["yeux-acc","Yeux"],["levres-acc","Lèvres"],["multi-acc","Multi-usage"]] },
  ],
};

const BUDGET_QUESTION = {
  key: "budget",
  label: "Votre budget par produit",
  options: [["low","Moins de 20 €"],["mid","20 – 40 €"],["high","Plus de 40 €"]],
};

const buildQuestions = (family) =>
  [FIRST_QUESTION, ...(FAMILY_QUESTIONS[family] || []), BUDGET_QUESTION];

const formatPrice = (p) => (p ? `${Number(p).toFixed(2).replace(".", ",")} €` : "—");

export const Quiz = ({ inline = false, onClose }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [accept, setAccept] = useState(false);
  const [emailErr, setEmailErr] = useState("");

  const questions = buildQuestions(answers.family);
  const total = questions.length;
  const isLast = step === total - 1;
  const current = questions[step];

  const choose = async (val) => {
    const next = { ...answers, [current.key]: val };
    setAnswers(next);
    if (!isLast) {
      setStep(step + 1);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quiz/recommend`, next);
      setResult(data);
    } catch (e) {
      setResult({ intro: "Désolé, recommandation indisponible.", products: [] });
    } finally {
      setLoading(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailErr("");
    if (!email.includes("@")) {
      setEmailErr("Email invalide");
      return;
    }
    try {
      await axios.post(`${API}/quiz/lead`, {
        email,
        name,
        answers,
        recommended_slugs: (result?.products || []).map((p) => p.slug),
        accepts_news: accept,
      });
      setEmailSent(true);
    } catch (err) {
      setEmailErr("Impossible d'enregistrer pour le moment.");
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setEmail("");
    setName("");
    setEmailSent(false);
  };

  const Body = (
    <div className={`fade-in-up ${inline ? "" : "p-8 md:p-12"} relative`} data-testid="quiz-body">
      {!inline && onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 grid place-items-center hover:bg-bl-soft transition-colors"
          data-testid="quiz-close"
        >
          <X size={18} strokeWidth={1.4} />
        </button>
      )}

      {!result ? (
        <>
          <span className="overline flex items-center gap-2">
            <Sparkles size={12} /> Quiz Beckyliss · Question {step + 1}/{total}
          </span>
          <h2 className="font-serif text-3xl md:text-4xl text-bl-text mt-3 mb-8 leading-tight">
            {current.label}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3" data-testid={`quiz-q-${current.key}`}>
            {current.options.map(([val, label]) => (
              <button
                key={val}
                disabled={loading}
                onClick={() => choose(val)}
                className="text-left px-5 py-4 border border-bl-border bg-bl-surface hover:border-bl-dore hover:bg-bl-rose/30 transition-colors text-sm tracking-wide disabled:opacity-50"
                data-testid={`quiz-opt-${current.key}-${val}`}
              >
                {label}
              </button>
            ))}
          </div>
          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              className="mt-8 text-xs overline hover:text-bl-text transition-colors"
              data-testid="quiz-back"
            >
              ← Retour
            </button>
          )}
          {loading && (
            <p className="mt-8 text-sm text-bl-textSoft" data-testid="quiz-loading">
              Beckyliss compose votre routine sur-mesure…
            </p>
          )}
        </>
      ) : (
        <>
          <span className="overline flex items-center gap-2">
            <Sparkles size={12} /> Votre routine sur-mesure
          </span>
          <h2 className="font-serif text-3xl md:text-4xl text-bl-text mt-3 leading-tight">
            Voici les pièces qui vous correspondent
          </h2>
          <p className="font-serif italic text-lg text-bl-textSoft mt-4 max-w-2xl leading-relaxed" data-testid="quiz-intro">
            « {result.intro} »
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="quiz-results">
            {result.products.map((p) => (
              <Link
                key={p.slug}
                to={`/produit/${p.slug}`}
                className="block bg-bl-surface border border-bl-border overflow-hidden group"
                data-testid={`quiz-product-${p.slug}`}
              >
                <div className="aspect-square bg-bl-soft overflow-hidden">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-full object-cover product-image-zoom" />
                  ) : null}
                </div>
                <div className="p-4">
                  <p className="font-serif text-sm leading-snug min-h-[3rem]">{p.name}</p>
                  <p className="text-xs text-bl-textSoft mt-2">{formatPrice(p.price)}</p>
                </div>
              </Link>
            ))}
          </div>

          {!emailSent ? (
            <form onSubmit={submitEmail} className="mt-12 border-t border-bl-border pt-10" data-testid="quiz-email-form">
              <div className="grid md:grid-cols-12 gap-6 items-end">
                <div className="md:col-span-6">
                  <span className="overline flex items-center gap-2"><Mail size={12} /> Recevoir mes recommandations</span>
                  <h3 className="font-serif text-2xl mt-2">Sauvegardez votre routine</h3>
                  <p className="text-sm text-bl-textSoft mt-2">
                    Optionnel. Nous vous enverrons un récapitulatif par e-mail et vous tiendrons informée des nouveautés Beckyliss.
                  </p>
                </div>
                <div className="md:col-span-6 space-y-3">
                  <input
                    type="text"
                    placeholder="Votre prénom (optionnel)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-transparent border-b border-bl-border focus:border-bl-dore outline-none py-2 text-sm"
                    data-testid="quiz-name-input"
                  />
                  <input
                    type="email"
                    required
                    placeholder="vous@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent border-b border-bl-border focus:border-bl-dore outline-none py-2 text-sm"
                    data-testid="quiz-email-input"
                  />
                  <label className="flex items-start gap-2 text-xs text-bl-textSoft cursor-pointer">
                    <input
                      type="checkbox"
                      checked={accept}
                      onChange={(e) => setAccept(e.target.checked)}
                      className="mt-0.5"
                      data-testid="quiz-news-checkbox"
                    />
                    J'accepte de recevoir les nouveautés Beckyliss (désinscription possible à tout moment).
                  </label>
                  {emailErr && <p className="text-xs text-red-700">{emailErr}</p>}
                  <button
                    type="submit"
                    className="bg-bl-text text-bl-bg px-6 py-3 text-sm tracking-wide hover:bg-bl-dore transition-colors inline-flex items-center gap-2"
                    data-testid="quiz-email-submit"
                  >
                    Recevoir ma routine <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div className="mt-12 border-t border-bl-border pt-10" data-testid="quiz-email-success">
              <span className="overline">Merci !</span>
              <p className="font-serif text-2xl mt-2">Votre routine vous attend dans votre boîte mail.</p>
              <p className="text-sm text-bl-textSoft mt-3">
                Une question ? Notre équipe vous répond sur WhatsApp.
              </p>
            </div>
          )}

          <button
            onClick={restart}
            className="mt-10 text-xs overline hover:text-bl-text"
            data-testid="quiz-restart"
          >
            ↻ Refaire le quiz
          </button>
        </>
      )}
    </div>
  );

  if (inline) return Body;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4" data-testid="quiz-modal">
      <div className="absolute inset-0 bg-bl-text/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-bl-bg border border-bl-border max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        {Body}
      </div>
    </div>
  );
};
